import React, { createContext, useContext, ReactNode } from 'react'
import { DentalDetails } from '../../../../../types/FirestoreUser'
import useDentalFormState from '../hooks/useDentalFormState'
import useDentalCalculations from '../hooks/useDentalCalculations'

// Type for the context value
type DentalFormContextType = ReturnType<typeof useDentalFormState> & {
  calculations: ReturnType<typeof useDentalCalculations>
}

// Create the context with an initial undefined value that has the correct type
const DentalFormContext = createContext<DentalFormContextType | undefined>(
  undefined
)

interface DentalFormProviderProps {
  children: ReactNode
  initialValues: Partial<DentalDetails>
  userAge?: number
}

/**
 * Provider component that wraps the dental form and provides state and calculations
 */
export const DentalFormProvider: React.FC<DentalFormProviderProps> = ({
  children,
  initialValues,
  userAge,
}) => {
  // Get form state with explicit type annotation
  const formState: ReturnType<typeof useDentalFormState> = useDentalFormState({
    initialValues,
  })

  // Get calculations based on form state
  const calculations = useDentalCalculations({
    firstAmalgamAge: formState.firstAmalgamAge,
    lastAmalgamAge: formState.lastAmalgamAge,
    amalgamTotalTeeth: formState.amalgamTotalTeeth,
    firstRootCanalAge: formState.firstRootCanalAge,
    lastRootCanalAge: formState.lastRootCanalAge,
    rootCanalsTotal: formState.rootCanalsTotal,
    removedTeeth: formState.removedTeeth,
    firstDentalImplantAge: formState.firstDentalImplantAge,
    lastDentalImplantAge: formState.lastDentalImplantAge,
    implantTotalTeeth: formState.implantTotalTeeth,
    userAge,
  })

  // Combine form state and calculations
  const contextValue: DentalFormContextType = {
    ...formState,
    calculations,
  }

  return (
    <DentalFormContext.Provider value={contextValue}>
      {children}
    </DentalFormContext.Provider>
  )
}

/**
 * Hook to use the dental form context
 */
export const useDentalForm = () => {
  const context = useContext(DentalFormContext)
  if (context === undefined) {
    throw new Error('useDentalForm must be used within a DentalFormProvider')
  }
  return context
}
